import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { TagInput } from '@nucleus-commerce/admin-kit/forms';
import type { Customer } from '@nucleus-commerce/api-client/admin';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Label } from '@nucleus-commerce/ui/components/label';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useUpdateCustomer } from '../hooks/use-customer-mutations';

export interface CustomerNotesCardProps {
  storeId: string;
  customer: Customer;
  readOnly?: boolean;
}

/** Tags save on every change, notes when the field loses focus. */
export function CustomerNotesCard({ storeId, customer, readOnly = false }: CustomerNotesCardProps) {
  const { t } = useTranslation('customers');
  const message = useErrorMessage();
  const update = useUpdateCustomer(storeId, 'customers:toasts.updated');
  const [notes, setNotes] = useState(customer.notes ?? '');
  // Resync when the profile dialog replaced the customer in the cache.
  const [seen, setSeen] = useState(customer.updatedAt);
  if (seen !== customer.updatedAt) {
    setSeen(customer.updatedAt);
    setNotes(customer.notes ?? '');
  }

  const save = (body: Parameters<typeof update.mutate>[0]['body']) =>
    update.mutate({ id: customer.id, body }, { onError: (error) => toast.error(message(error)) });

  return (
    <Card className="gap-3" data-tour="customers.detail.notes">
      <CardHeader>
        <CardTitle>{t('notes.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="customer-tags">{t('notes.tags')}</Label>
          <TagInput
            id="customer-tags"
            value={customer.tags}
            onChange={(tags) => save({ tags })}
            placeholder={t('notes.tagsPlaceholder')}
            removeLabel={(tag) => t('notes.removeTag', { tag })}
            disabled={readOnly || update.isPending}
            max={50}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="customer-notes">{t('notes.notes')}</Label>
          <Textarea
            id="customer-notes"
            rows={4}
            value={notes}
            placeholder={t('notes.notesPlaceholder')}
            disabled={readOnly}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => {
              const next = notes.trim() === '' ? null : notes;
              if (next !== customer.notes) save({ notes: next });
            }}
          />
          <p className="text-muted-foreground text-xs">{t('notes.notesHint')}</p>
        </div>
      </CardContent>
    </Card>
  );
}
